import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {Text, TextSize} from '@Components/text';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import type {GenerateAISpeechResponse} from '@Libraries/text-to-speech.library';
import {getUrlForGeneratedAiSpeech, getUserGeneratedSpeech} from '@Libraries/text-to-speech.library';
import {getAssetUrl} from '@Utils/s3.util';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {GeneratedVoiceItem} from '@Panels/ai-voice-panel/components/generated-voice-item';
import {getAvatarUrlForVoice} from './generated-voice-list.library';
import type {GeneratedVoiceListProps} from './generated-voice-list.types';
import {GENERATED_VOICE_GRID_ID, GENERATED_VOICE_GRID_ITEM_HEIGHT, GENERATED_VOICE_GRID_ITEM_WIDTH} from './generated-voice-list.types';
import styles from './generated-voice-list.module.scss';
import {getNoOfGridItems} from '@Components/base-grid';
import {useAppSelector} from '@/hooks';
import {isMobile} from 'react-device-detect';

export function GeneratedVoiceList({isGenerating, ...props}: GeneratedVoiceListProps): ReactElement<any> | null {
  const [noGeneratedAudioInfographicLoaded, setNoGeneratedAudioInfographicLoaded] = useState<boolean>(false);
  const noOfGeneratedItems = useAppSelector((state) => {
    return getNoOfGridItems(state, GENERATED_VOICE_GRID_ID);
  });

  const getItems = async (params: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    const offset = Object.keys(params.offsets).reduce((agg, item) => {
      return agg + params.offsets[item];
    }, 0);

    let items: GenerateAISpeechResponse[] = [];

    try {
      items = await getUserGeneratedSpeech(offset, params.limit);
    } catch {
      items = [];
    }

    const gridItems: GridItemStorage[] = items.map((item) => {
      const audioSrc = getUrlForGeneratedAiSpeech(item.filename);
      return {
        type: GRID_ITEM_TYPE.CUSTOM,
        id: `${item.id}`,
        audioSrc,
        name: item.name,
        duration: `${item.duration}`,
        data: {
          hashedFilename: item.filename,
        },
        children: (
          <GeneratedVoiceItem
            id={`${item.id}`}
            title={item.name}
            duration={item.duration}
            dateLabel={item.generatedOn}
            source={audioSrc}
            avatarUrl={getAvatarUrlForVoice(item.idVoice)}
          />
        ),
      };
    });

    return {response: gridItems, offsetsData: {DEFAULT_SOURCE: gridItems.length}};
  };

  const onGridItemClicked = (item: GridItemStorage): void => {
    if (!item.children.props.source) {
      return;
    }
    void props.onAddAudioItem(item.id);
  };

  const getEmptyGridImage = (): ReactElement => {
    return (
      <img
        onLoad={() => {
          setNoGeneratedAudioInfographicLoaded(true);
        }}
        src={getAssetUrl('poster-editor-thumbnails/text-to-speech-empty-infographic.png')}
        alt={window.i18next.t('pmwjs_ai_text_to_speech_infographic_text')}
        className={noGeneratedAudioInfographicLoaded ? styles.emptyVisual : '_hidden'}
      />
    );
  };

  const getSkeletonBrickForEmptyGrid = (): ReactElement | null => {
    if (!noGeneratedAudioInfographicLoaded) {
      return <SkeletonBrick className={styles.imagePlaceholder} isPulsating />;
    }
    return null;
  };

  const getEmptyGridInfographic = (): ReactElement | null => {
    if (noOfGeneratedItems || isGenerating) {
      return null;
    }
    return (
      <div className="_full-width _full-height flex-v-row flex-center">
        {getEmptyGridImage()}
        {getSkeletonBrickForEmptyGrid()}
        <Text className="spacing-p-t-2" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_ai_text_to_speech_infographic_text')} />
      </div>
    );
  };

  return (
    <>
      {getEmptyGridInfographic()}
      <LazyMasonryGrid
        gridId={GENERATED_VOICE_GRID_ID}
        gridSelectionGroupId={GENERATED_VOICE_GRID_ID}
        getItems={getItems}
        clearGridOnUnmount
        customOnGridItemClicked={isMobile ? undefined : onGridItemClicked}
        maxSelectionNumber={isMobile ? 1 : undefined}
        showSelectionOverlay={!isMobile}
        className={`_full-width spacing-p-t-8 spacing-p-b-4`}
        makeContentScrollable={false}
        customDimensions={{
          width: GENERATED_VOICE_GRID_ITEM_WIDTH,
          height: GENERATED_VOICE_GRID_ITEM_HEIGHT,
        }}
        showHoverOverlay={false}
      />
    </>
  );
}
